import {
    FormControl,
    FormControlLabel,
    Grid,
    Radio,
    RadioGroup,
    Typography
} from "@mui/material"
import { useEffect, useMemo, useState } from "react"
import DebugService from "../services/DebugService"
import {
    BLOCK_CONTAINER_ICON,
    CONST_SITE_LABEL,
    STORAGE_KEYS,
    setScopedItem
} from "./common/Helper"
import RoiApiService from "../services/RoiApiService"
import DataGridTable from "./common/DataGridTable"
import { useScopedLocalStorage } from "../hooks/useScopedLocalStorage"
import { BlockROIContainer } from "./common/BlockROIContainer"
import { useAuth } from "../state/AuthContext"

/**
 * ROI Saving Plan Recommendations
 * @returns 
 */
export const ROI = () => {
    const [data, setData] = useState({})
    const [tableRowList, setTableRowList] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [formData, setFormData] = useState({
        SavingsPlansType: 'EC2_INSTANCE_SP',
        PaymentOption: 'ALL_UPFRONT',
        LookbackPeriodInDays: 'SEVEN_DAYS',
        TermInYears: 'ONE_YEAR'
    })

    const { getScopedItem } = useScopedLocalStorage()

    const { selectedAccount } = useAuth()

    const tableColumnList = [
        {
            field: 'Region',
            headerName: 'Region',
            flex: 1,
            align: 'center'
        },
        {
            field: 'InstanceFamily',
            headerName: 'Instance Family',
            flex: 1,
            align: 'center'
        },
        {
            field: 'HourlyCommitmentToPurchase',
            headerName: 'Commitment',
            flex: 1,
            align: 'center',
            renderCell: params => (
                <span>${(parseInt(params['value'] ?? 0)).toFixed(2)}/hour</span>
            )
        },
        {
            field: 'EstimatedSavingsAmount',
            headerName: 'Estimated Savings',
            flex: 1,
            align: 'center',
            renderCell: params => (
                <span>${(parseInt(params['value'] ?? 0)).toFixed(2)} ({(parseInt(params['row']['EstimatedSavingsPercentage'] ?? 0)).toFixed(2)}%)</span>
            )
        },
    ]

    /**
     * Fetch user data
     */
    const fetchData = () => {
        setIsLoading(true)

        const dataRequest = {
            ...formData,
            userAccountId: selectedAccount
        }

        const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)
        const parseToJson = JSON.parse(userDetails)

        if (parseToJson['userId']) {
            dataRequest['userId'] = parseToJson['userId']
            // dataRequest['userId'] = 1
        }

        DebugService.log('ROI Component: Request initiated', dataRequest)

        RoiApiService.getSavingPlans(dataRequest)
            .then(res => {
                DebugService.log(`ROI Component: API response`, res)
                const apiData = res['data'] ?? {}

                if (res['error']) {
                    DebugService.error(`ROI Component: Error caught while fetching data`, res['error'])
                } else {
                    setData(apiData)

                    if (apiData['SavingsPlansPurchaseRecommendationDetails']) {
                        setTableRowList(
                            apiData['SavingsPlansPurchaseRecommendationDetails'].map(d => ({
                                id: d['RecommendationDetailId'],
                                Region: d['SavingsPlansDetails']['Region'],
                                InstanceFamily: d['SavingsPlansDetails']['InstanceFamily'],
                                HourlyCommitmentToPurchase: d['HourlyCommitmentToPurchase'],
                                EstimatedSavingsAmount: d['EstimatedSavingsAmount'],
                                EstimatedSavingsPercentage: d['EstimatedSavingsPercentage'],
                            }))
                        )
                    }
                }

                setIsLoading(false)
            })
            .catch(err => {
                DebugService.error(`ROI Component: Error caught while fetching data`, err)
                setIsLoading(false)
            })
    }

    useMemo(() => {
        fetchData()
    }, [formData])

    /**
     * Call useEffect on page load
     */
    useEffect(() => {
        document.title = `${CONST_SITE_LABEL} | ROI Recommendations`
        fetchData()
    }, [])

    /**
    * @description Stores the latest API response in storage.
    * @author Ashish Sharma
    */
    useEffect(()=>{
        setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, data)
    },[data])
    
    const RadioFormControl = ({ label, value }) => {
        return (
            <FormControlLabel
                value={value}
                control={
                    <Radio
                        size="small"
                        sx={{
                            color: '#506C84',
                            '&.Mui-checked': {
                                color: '#506C84',
                            },
                        }} />
                }
                label={label}
                style={{
                    borderRadius: '4px',
                    border: '1px solid #A7BBCE',
                    paddingRight: '10px',
                    flex: 1
                }}
                sx={{
                    '& .MuiFormControlLabel-label': {
                        color: '#425466', 
                    },
                }}
            />
        )
    }

    return (
        <Grid container spacing={1} className='roi-page'>
            {/* Before Recommended Purchase */}
            {data['SavingsPlansPurchaseRecommendationSummary'] && (
                <Grid item xs={12} md={4}>
                    <BlockROIContainer
                        order={2}
                        headerLabel={'Before Recommended Purchase'}
                        displayIcon={BLOCK_CONTAINER_ICON.SAVING}
                        displayValue={parseFloat((data['SavingsPlansPurchaseRecommendationSummary']['CurrentMonthlyOnDemandSpendAmount'] ?? 0)).toFixed(2)}
                        sideText={`${parseFloat((data['SavingsPlansPurchaseRecommendationSummary']['CurrentMonthlyOnDemandSpendAmountPerHour'] ?? 0)).toFixed(2)} per hour`}
                        displayLabel={'Current monthly on-demand spend'}
                        unitValue={'$'}
                    />
                </Grid>
            )}
            {data['costWithoutBenefit'] && (
                <Grid item xs={12} md={4}>
                    <BlockROIContainer
                        order={2}
                        headerLabel={'Before Recommended Purchase'}
                        displayIcon={BLOCK_CONTAINER_ICON.SAVING}
                        displayValue={parseFloat((data['costWithoutBenefit'] ?? 0)).toFixed(2)}
                        sideText=""
                        displayLabel={'Current monthly on-demand spend'}
                        unitValue={'$'}
                    />
                </Grid>
            )}

            {/* After Recommended Purchase */}
            {data['SavingsPlansPurchaseRecommendationSummary'] && (
                <>
                    <Grid item xs={12} md={4}>
                        <BlockROIContainer
                            order={1}
                            headerLabel={'After Recommended Purchase'}
                            displayIcon={BLOCK_CONTAINER_ICON.SAVING}
                            displayValue={parseFloat((data['SavingsPlansPurchaseRecommendationSummary']['EstimatedMonthlySpendAmount'] ?? 0)).toFixed(2)}
                            sideText={`${parseFloat((data['SavingsPlansPurchaseRecommendationSummary']['EstimatedMonthlySpendAmountPerHour'] ?? 0)).toFixed(2)} per hour`}
                            displayLabel={'Estimated monthly spend'}
                            unitValue={'$'}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <BlockROIContainer
                            order={3}
                            headerLabel={'After Recommended Purchase'}
                            displayIcon={BLOCK_CONTAINER_ICON.SAVING_HAND}
                            displayValue={parseFloat((data['SavingsPlansPurchaseRecommendationSummary']['EstimatedMonthlySavingsAmount'] ?? 0)).toFixed(2)}
                            sideText={`${parseFloat((data['SavingsPlansPurchaseRecommendationSummary']['EstimatedMonthlySavingsAmountPerHour'] ?? 0)).toFixed(2)} per hour`}
                            displayLabel={'Estimated monthly savings'}
                            unitValue={'$'}
                        />
                    </Grid>
                </>
            )}

            <Grid item container spacing={1} xs={12} rowGap={'20px'}>
                <Grid item xs={12}>
                    <FormControl className="full-width">
                        <RadioGroup
                            row
                            aria-labelledby="demo-row-radio-buttons-group-label"
                            name="savingsPlansType"
                            value={formData['SavingsPlansType']}
                            onChange={e => {
                                setFormData(prev => ({
                                    ...prev,
                                    SavingsPlansType: e['target']['defaultValue']
                                }))
                            }}
                        >
                            <RadioFormControl label={'Compute Savings Plans'} value={'COMPUTE_SP'} />
                            <RadioFormControl label={'EC2 Instance Savings Plans'} value={'EC2_INSTANCE_SP'} />
                            <RadioFormControl label={'SageMaker Savings Plans'} value={'SAGEMAKER_SP'} />
                        </RadioGroup>
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={3} textAlign={'center'} padding={'10px'}>
                    <div className="recommended-option-label">
                        <Typography variant="subtitle2" color={'#012A4A'} fontWeight={'600'}>Recommendation Options</Typography>
                        <img src="/roi-icon.svg" alt="ROI Icon" />
                    </div>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" className="filter-label">Savings Plans term</Typography>
                    <FormControl className="full-width">
                        <RadioGroup
                            row={false}
                            aria-labelledby="demo-row-radio-buttons-group-label"
                            name="TermInYears"
                            value={formData['TermInYears']}
                            onChange={e => {
                                setFormData(prev => ({
                                    ...prev,
                                    TermInYears: e['target']['defaultValue']
                                }))
                            }}
                            sx={{ gap: '8px' }}
                        >
                            <RadioFormControl label={'1-year'} value={'ONE_YEAR'} />
                            <RadioFormControl label={'3-year'} value={'THREE_YEARS'} />
                        </RadioGroup>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" className="filter-label">Payment option</Typography>
                    <FormControl className="full-width">
                        <RadioGroup
                            row={false}
                            aria-labelledby="demo-row-radio-buttons-group-label"
                            name="PaymentOption"
                            value={formData['PaymentOption']}
                            onChange={e => {
                                setFormData(prev => ({
                                    ...prev,
                                    PaymentOption: e['target']['defaultValue']
                                }))
                            }}
                            sx={{ gap: '8px' }}
                        >
                            <RadioFormControl label={'All upfront'} value={'ALL_UPFRONT'} />
                            <RadioFormControl label={'Partial upfront'} value={'PARTIAL_UPFRONT'} />
                            <RadioFormControl label={'No upfront'} value={'NO_UPFRONT'} />
                        </RadioGroup>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" className="filter-label">Based on the last</Typography>
                    <FormControl className="full-width">
                        <RadioGroup
                            row={false}
                            aria-labelledby="demo-row-radio-buttons-group-label"
                            name="LookbackPeriodInDays"
                            value={formData['LookbackPeriodInDays']}
                            onChange={e => {
                                setFormData(prev => ({
                                    ...prev,
                                    LookbackPeriodInDays: e['target']['defaultValue']
                                }))
                            }}
                            sx={{ gap: '8px' }}
                        >
                            <RadioFormControl label={'7 days'} value={'SEVEN_DAYS'} />
                            <RadioFormControl label={'30 days'} value={'THIRTY_DAYS'} />
                            <RadioFormControl label={'60 days'} value={'SIXTY_DAYS'} />
                        </RadioGroup>
                    </FormControl>
                </Grid>

            </Grid>

            <Grid item xs={12}>
                <DataGridTable
                    evenRowBgColor={''}
                    headerBgColor={'#506C84'}
                    actionIconColor={'#506C84'}
                    evenRowColor={'#506C84'}
                    columns={tableColumnList}
                    rows={tableRowList}
                    showTooltip={false}
                    showPagination={true}
                />
            </Grid>
        </Grid>
    )
}